"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { CONTACT_SALES_URL, FREE_TRIAL_URL, FREE_TRIAL_LABEL } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";

type CtaLink = { text: string; href: string };

type BlogCtaProps = {
  label?: string;
  heading?: string;
  subheading?: string;
  primaryCta?: CtaLink;
  secondaryCta?: CtaLink;
  className?: string;
};

export function BlogCta({
  label = "Ready to launch?",
  heading = "Proxies built for real operations.",
  subheading = "For teams that depend on stability, not luck.",
  primaryCta = { text: FREE_TRIAL_LABEL, href: FREE_TRIAL_URL },
  secondaryCta = { text: "Talk to Sales", href: CONTACT_SALES_URL },
  className,
}: BlogCtaProps) {
  const maintenanceMode = useMaintenanceMode();
  const primaryHref = maintenanceMode ? MAINTENANCE_PATH : primaryCta.href;
  return (
    <div
      className={cn(
        // Dark-fixed card: local token overrides keep it dark in both themes
        "[--background:#0a0a0b] [--border:#27272a] [--brand-green:#5b8cff] [--card:#111113] [--foreground:#f7f7f8] [--muted-foreground:#a0a0a0]",
        "border-border bg-card relative mt-15 mb-0 overflow-hidden rounded-2xl border",
        className
      )}
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse at top left, rgba(91,140,255,0.08) 0%, transparent 60%)",
        }}
      />
      <div className="relative px-8 py-10 sm:px-10">
        <p className="mb-3 text-xs font-semibold text-[var(--brand-green)]">{label}</p>
        <h2 className="text-foreground mb-3 max-w-sm text-2xl leading-tight font-black sm:text-3xl">
          {heading}
        </h2>
        <p className="text-muted-foreground mb-7 text-sm">{subheading}</p>
        <div className="flex flex-wrap gap-3">
          <Link
            href={primaryHref}
            className="bg-foreground text-background inline-flex items-center rounded-lg px-5 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
          >
            {primaryCta.text}
          </Link>
          <Link
            href={secondaryCta.href}
            className="border-border text-foreground hover:border-foreground/40 hover:bg-foreground/5 inline-flex items-center rounded-lg border px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            {secondaryCta.text}
          </Link>
        </div>
      </div>
    </div>
  );
}
